import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/translations';
import SergicLogo from './SergicLogo';
import LanguageSelector from './LanguageSelector';

interface MobileHeaderProps {
  onMenuClick: () => void;
  language: 'fr' | 'en';
  onLanguageChange: (language: 'fr' | 'en') => void;
}

const MobileHeader: React.FC<MobileHeaderProps> = ({ 
  onMenuClick, 
  language, 
  onLanguageChange 
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const t = useTranslation(language);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowUserMenu(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-2 py-2 relative">
      <div className="flex items-center justify-between">
        {/* Left side - Menu button and Logo */}
        <div className="flex items-center min-w-0 flex-1">
          <button
            onClick={onMenuClick}
            className="mr-2 p-1.5 rounded-lg hover:bg-gray-100 flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center min-w-0">
            <SergicLogo className="h-5 flex-shrink-0" />
            <h1 className="text-xs sm:text-sm font-semibold text-gray-900 truncate ml-1">
              {t('copropriety.assistant')}
            </h1>
          </div>
        </div>

        {/* Right side - User menu only, language selector moved to sidebar */}
        <div className="flex items-center">
          {/* User menu button */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center p-1.5 rounded-lg hover:bg-gray-100"
            >
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-medium">
                  {user?.firstName ? user.firstName[0].toUpperCase() : user?.email?.[0].toUpperCase()}
                </span>
              </div>
            </button>

            {/* User dropdown menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="py-2">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
                    </p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  
                  {/* Language selector in menu */}
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Langue</p>
                    <LanguageSelector language={language} onLanguageChange={onLanguageChange} />
                  </div>
                  
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Profil
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {t('logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay to close user menu when clicking outside */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
};

export default MobileHeader;
